import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from "react-helmet-async";
import { ChevronLeft, Share2, Bookmark, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogDetail = () => {

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-[#0E2A62] selection:text-white">
            <Helmet>
                <title>The Future of Enterprise AI & ServiceNow Consulting 2026 | Mobikode</title>
            </Helmet>


            {/* --- Hero Section: Matched with Blog Listing Hero Theme --- */}
            <section className="relative w-full pt-32 pb-24 flex items-center bg-[#000814] overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/new-service/bg1.png"
                        alt="Background"
                        className="w-full h-full object-cover opacity-80"
                    />
                    {/* Gradient Overlay matching your Blog Listing theme */}
                    <div
                        style={{ background: "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)" }}
                        className="absolute inset-0"
                    ></div>
                </div>

                <div className="relative z-10 max-w-[900px] mx-auto px-6">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-blue-300 font-semibold hover:text-white transition-all mb-10 group">
                        <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
                        <span className="text-sm uppercase text-[14px] lg:text-[16px] font-[500] font-graphik tracking-widest">Back to Insights</span>
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="bg-white text-[#0E2A62] px-4 py-1.5 rounded-full text-[14px] lg:text-[16px] font-[500] font-graphik uppercase tracking-wider">
                                Digital Strategy
                            </span>
                            <span className="text-white/30 text-sm">•</span>
                            <span className="text-gray-300 text-[14px] lg:text-[16px] font-[500] font-graphik">8 Min Read</span>
                        </div>

                        {/* Title in White with Sora Font consistency */}
                        <h1 className="text-white text-[20px] md:text-[30px] font-sora font-[600] leading-[1.15] mb-8">
                            The Future of Digital Transformation in <span className="text-blue-400">2026</span>
                        </h1>

                        <div className="flex items-center justify-between py-8 border-t border-white/10">
                            <div className="flex items-center gap-4 font-sora text-white">
                                <div className="w-12 h-12 rounded-full bg-[#0E2A62] border border-white/20 flex items-center justify-center font-bold text-lg shadow-xl">
                                    M
                                </div>
                                <div>
                                    <p className="font-[600] lg:text-[20px] text-[16px] font-sora">Mobikode Team</p>
                                    <p className="text-gray-400 text-[14px] lg:text-[16px] font-[500] font-graphik">March 3, 2026</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <button className="p-2.5 rounded-full border border-white/20 hover:bg-white/10 transition-colors text-white">
                                    <Share2 size={18} />
                                </button>
                                <button className="p-2.5 rounded-full border border-white/20 hover:bg-white/10 transition-colors text-white">
                                    <Bookmark size={18} />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* --- Featured Image: Layered on top of Hero Transition --- */}
            <div className="max-w-[1000px] mx-auto px-6 -mt-12 relative z-20">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="overflow-hidden shadow-2xl"
                >
                    <img
                        src="/assets/v/item7.jpg"
                        alt="Digital Transformation 2026"
                        className="w-full h-[300px] md:h-[550px] object-cover hover:scale-105 transition-transform duration-700"
                    />
                </motion.div>
            </div>

            {/* --- Main Content Section (White Background) --- */}
            <section className="lg:py-20 py-10 bg-white">
                <div className="max-w-[1200px] mx-auto px-6 flex flex-col lg:flex-row gap-16">

                    <aside className="lg:w-1/4 hidden lg:block h-fit sticky top-32">
                        <div className="p-6 bg-gray-50 font-graphik font-[500] lg:text-[16px] text-[14px] rounded-2xl border border-gray-100">
                            <h4 className="text-[#0E2A62] font-bold font-sora mb-4">In this article</h4>
                            <nav className="space-y-3 font-graphik font-[500] lg:text-[16px] text-[14px]">
                                <a href="#intro" className="block text-blue-600 font-medium">01. Overview</a>
                                <a href="#ecosystems" className="block text-gray-500 hover:text-[#0E2A62] transition-colors">02. Intelligent Ecosystems</a>
                                <a href="#cloud" className="block text-gray-500 hover:text-[#0E2A62] transition-colors">03. Cloud Scalability</a>
                                <a href="#conclusion" className="block text-gray-500 hover:text-[#0E2A62] transition-colors">04. Final Thoughts</a>
                            </nav>
                        </div>
                    </aside>

                    <div className="lg:w-3/4 max-w-[800px] font-graphik">
                        <div className="prose prose-lg prose-slate max-w-none prose-headings:text-[#0E2A62] prose-headings:font-bold prose-headings:font-sora prose-p:text-gray-600 prose-p:font-graphik prose-p:leading-relaxed">

                            <p className="lg:text-[20px] text-[16px] leading-snug text-[#0E2A62]/80 font-[600] font-sora mb-12">
                                Digital transformation is no longer a choice; it's a necessity for survival in the modern enterprise landscape. As we look toward 2026, the benchmarks for success have evolved.
                            </p>

                            <h2 className='lg:text-[30px] text-[20px] leading-snug text-[#0E2A62] font-[600] font-sora'>The Rise of Intelligent Ecosystems</h2>
                            <p className="text-[14px] lg:text-[16px] font-[500] font-graphik">
                                As we move further into 2026, the focus has shifted from simple automation to building intelligent ecosystems. Mobikode is at the forefront of this shift, helping enterprises integrate AI-driven decision-making into their core processes.
                            </p>

                            <div className="relative my-16 p-10 bg-[#0E2A62] overflow-hidden group font-sora">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 group-hover:scale-110 transition-transform" />
                                <blockquote className="relative z-10 border-none p-0 m-0">
                                    <p className="text-white lg:text-[20px] text-[16px] font-sora font-[600] leading-normal italic">
                                        Technology is best when it brings people together and solves complex problems with simple, elegant solutions.
                                    </p>
                                    <footer className="text-blue-200 mt-6 text-[14px] lg:text-[16px] font-[500] font-graphik">— Engineering Lead at Mobikode</footer>
                                </blockquote>
                            </div>

                            <h2 className='lg:text-[30px] text-[20px] leading-snug text-[#0E2A62] font-[600] font-sora'>Modernizing Legacy for Performance</h2>
                            <p className="text-[14px] lg:text-[16px] font-[500] font-graphik">
                                Legacy systems often act as anchors for growing businesses. Our engineering teams specialize in deconstructing monolithic architectures into microservices that are ready for the high-demand world of 2026.
                            </p>

                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 my-10 list-none p-0">
                                {['Cloud-Native Architecture', 'AI-Integrated Testing', 'Edge Computing Ready', 'Security First Mindset'].map((item) => (
                                    <li key={item} className="bg-blue-50 p-4 flex items-center gap-3 text-[#0E2A62] font-[600] lg:text-[20px] text-[16px] font-sora">
                                        <div className="w-2 h-2 rounded-full bg-[#0E2A62]" />
                                        {item}
                                    </li>
                                ))}
                            </ul>

                            <h2 className='lg:text-[30px] text-[20px] leading-snug text-[#0E2A62] font-[600] font-sora'>Conclusion</h2>
                            <p className="text-[14px] lg:text-[16px] font-[500] font-graphik">
                                Embracing these changes requires a strategic partner. Mobikode’s approach ensures that your transformation journey is scalable, secure, and results-oriented.
                            </p>
                        </div>

                        <div className="py-10 border-t border-gray-100">
                            <div className="flex flex-wrap items-center justify-between gap-6">
                                <div className="flex gap-2">
                                    {["AI", "Cloud", "Modernization"].map(tag => (
                                        <span key={tag} className="text-[12px] bg-gray-100 hover:bg-gray-200 text-[14px] lg:text-[16px] font-[500] font-graphik cursor-pointer px-4 py-1.5 rounded-lg text-gray-500 transition-colors">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex items-center gap-4 text-[14px] lg:text-[16px] font-[500] font-graphik text-gray-400">
                                    <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#0E2A62] transition-colors"><MessageSquare size={16} /> 12</span>
                                    <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#0E2A62] transition-colors"><Share2 size={16} /> Share</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogDetail;